import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import type { VpeLine } from '@growthdept/blocks/gift-pricing';
import { brand, formatDateNL, formatEur } from '@/lib/pdf-brand';
import { supplierProductIdentity } from '@/lib/product-search';
import type { Client, GiftPackage, GiftProduct, GiftSupplier, Project } from '@/lib/types';

type LineWithProduct = VpeLine<GiftProduct>;

interface Props {
  project: Project;
  pkg: GiftPackage;
  client: Client | null;
  supplier: GiftSupplier;
  vpeLines: LineWithProduct[];
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: brand.textBody,
  },
  headerBar: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: brand.primary,
  },
  brandBlock: { flexDirection: 'column' },
  brandTagline: { color: brand.textMuted, fontSize: 8, textTransform: 'uppercase', letterSpacing: 1 },
  brandName: { color: brand.primary, fontSize: 18, fontWeight: 700, marginTop: 2 },
  brandAccentBar: { width: 60, height: 4, marginTop: 4, backgroundColor: brand.accent },
  docTitle: { textAlign: 'right' },
  docTitleLabel: { color: brand.textMuted, fontSize: 8, textTransform: 'uppercase', letterSpacing: 1 },
  docTitleName: { color: brand.primary, fontSize: 16, fontWeight: 700, marginTop: 2 },
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 18 },
  metaBlock: { flex: 1 },
  metaLabel: { color: brand.textMuted, fontSize: 8, textTransform: 'uppercase', letterSpacing: 1 },
  metaValue: { color: brand.primary, fontSize: 12, marginTop: 2 },
  metaSub: { color: brand.textBody, fontSize: 10, marginTop: 1 },
  tableHead: {
    flexDirection: 'row',
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: brand.primary,
    marginTop: 22,
  },
  tableCell: { fontSize: 8, color: brand.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  itemRow: { flexDirection: 'row', paddingVertical: 4, borderBottomWidth: 0.5, borderBottomColor: brand.border },
  sku: { width: 72 },
  name: { flex: 3 },
  brandSub: { color: brand.textMuted, fontSize: 8, marginTop: 1 },
  col: { textAlign: 'right' },
  total: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
    paddingTop: 8,
    borderTopWidth: 2,
    borderTopColor: brand.primary,
  },
  totalLabel: { color: brand.primary, fontSize: 13, fontWeight: 700 },
  totalValue: { color: brand.accent, fontSize: 16, fontWeight: 700 },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: brand.textMuted,
    fontSize: 8,
  },
});

export function SupplierOrderPdf({ project, pkg, client, supplier, vpeLines }: Props) {
  const items = vpeLines.filter((l) => l.product.supplier_id === supplier.id);
  const total = items.reduce((s, l) => s + l.total_purchase_cost, 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerBar}>
          <View style={styles.brandBlock}>
            <Text style={styles.brandTagline}>Inkoopopdracht</Text>
            <Text style={styles.brandName}>2BMade</Text>
            <View style={styles.brandAccentBar} />
          </View>
          <View style={styles.docTitle}>
            <Text style={styles.docTitleLabel}>Leverancier</Text>
            <Text style={styles.docTitleName}>{supplier.name}</Text>
          </View>
        </View>

        <View style={styles.metaRow}>
          <View style={styles.metaBlock}>
            <Text style={styles.metaLabel}>Project</Text>
            <Text style={styles.metaValue}>{project.name}</Text>
            {client?.company_name && (
              <Text style={styles.metaSub}>voor {client.company_name}</Text>
            )}
            <Text style={styles.metaSub}>
              {pkg.quantity} pakketten · ref {project.id.slice(0, 8)}
            </Text>
          </View>
          <View style={[styles.metaBlock, { alignItems: 'flex-end' }]}>
            <Text style={styles.metaLabel}>Besteldatum</Text>
            <Text style={styles.metaValue}>{formatDateNL(new Date())}</Text>
            {pkg.handling_date && (
              <Text style={styles.metaSub}>uiterlijk leveren {formatDateNL(pkg.handling_date)}</Text>
            )}
          </View>
        </View>

        <View style={styles.tableHead}>
          <Text style={[styles.tableCell, styles.sku]}>Art.nr</Text>
          <Text style={[styles.tableCell, styles.name]}>Artikel</Text>
          <Text style={[styles.tableCell, styles.col, { width: 50 }]}>Per pakket</Text>
          <Text style={[styles.tableCell, styles.col, { width: 40 }]}>Colli</Text>
          <Text style={[styles.tableCell, styles.col, { width: 50 }]}>Te bestellen</Text>
          <Text style={[styles.tableCell, styles.col, { width: 60 }]}>Stuksprijs</Text>
          <Text style={[styles.tableCell, styles.col, { width: 60 }]}>Subtotaal</Text>
        </View>

        {items.length === 0 ? (
          <Text style={{ marginTop: 12, color: brand.textMuted }}>
            Geen artikelen van deze leverancier in dit pakket.
          </Text>
        ) : (
          items.map((l, idx) => {
            const identity = supplierProductIdentity(l.product);
            return (
              <View key={idx} style={styles.itemRow}>
                <View style={styles.sku}>
                  <Text style={{ color: brand.textMuted, fontSize: 9 }}>
                    {identity.articleNumber ?? 'Artikelcode ontbreekt'}
                  </Text>
                  {identity.ean && <Text style={styles.brandSub}>EAN {identity.ean}</Text>}
                </View>
                <View style={styles.name}>
                  <Text>{l.product.name}</Text>
                  {l.product.brand && <Text style={styles.brandSub}>{l.product.brand}</Text>}
                </View>
                <Text style={[styles.col, { width: 50 }]}>{l.quantity_per_package}×</Text>
                <Text style={[styles.col, { width: 40, color: brand.textMuted }]}>{l.product.vpe}</Text>
                <Text style={[styles.col, { width: 50, color: brand.primary, fontWeight: 700 }]}>
                  {l.total_ordered}
                </Text>
                <Text style={[styles.col, { width: 60, color: brand.textMuted }]}>
                  {formatEur(l.unit_purchase_price)}
                </Text>
                <Text style={[styles.col, { width: 60, fontWeight: 700 }]}>
                  {formatEur(l.total_purchase_cost)}
                </Text>
              </View>
            );
          })
        )}

        {items.length > 0 && (
          <View style={styles.total}>
            <Text style={styles.totalLabel}>Totale inkoop ex btw</Text>
            <Text style={styles.totalValue}>{formatEur(total)}</Text>
          </View>
        )}

        <View style={styles.footer} fixed>
          <Text>2BMade · inkoopopdracht · {formatDateNL(new Date())}</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}
